class FormOptionSelect extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._form = document.getElementById(this.getAttribute('data-form'));
    if (!this._form) return;

    this._radios = Array.from(this.querySelectorAll('input[type="radio"]'));
    this._radios.forEach((radio) => {
      radio.addEventListener('change', () => this._sync(), { signal: this._signal });
    });

    this._setup?.();
    this._sync();
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get _signal() {
    return this._abortController.signal;
  }
  get _mode() {
    return this._radios.find((radio) => radio.checked)?.value ?? '';
  }
  _sync() {
    const input = this._form.querySelector(`input[name="${this._field}"]`);
    if (input) input.value = this._mode;
    this._apply(this._mode);
  }
}

class FilingTypeSelect extends FormOptionSelect {
  get _field() { return 'filing_type'; }

  _apply(mode) {
    this._form.querySelectorAll('[data-filing-type]').forEach((group) => {
      const active = group.getAttribute('data-filing-type') === mode;
      group.classList.toggle('hidden', !active);
      group.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !active;
      });
    });
  }
}
if (!window.customElements.get('filing-type-select')) {
  window.customElements.define('filing-type-select', FilingTypeSelect);
}

function currentReportingPeriod(now = new Date()) {
  const year = now.getFullYear();
  return now.getMonth() >= 6
    ? { start: year - 1, end: year }
    : { start: year - 2, end: year - 1 };
}

class ReportingPeriodSelect extends FormOptionSelect {
  get _field() { return 'reporting_type'; }

  _setup() {
    this._message = this.querySelector('[data-period-message]');
    this._notice = this._form.querySelector('[data-period-notice]');
    this._noticeWrapper = this._form.querySelector('[data-period-notice-wrapper]');
    this._start = this._form.querySelector('[data-reporting-year="start"]');
    this._end = this._form.querySelector('[data-reporting-year="end"]');
    this._years = [this._start, this._end].filter(Boolean);

    const maxYear = currentReportingPeriod().end;
    this._years.forEach((field) => {
      field.max = maxYear;
      field.addEventListener('input', () => this._render(), { signal: this._signal });
      field.addEventListener('change', (event) => {
        this._render();
        if (!event.target.checkValidity()) event.target.reportValidity();
      }, { signal: this._signal });
    });
  }

  get _period() {
    const start = parseInt(this._start?.value, 10);
    const end = parseInt(this._end?.value, 10);
    return {
      start: Number.isFinite(start) ? start : null,
      end: Number.isFinite(end) ? end : null
    };
  }

  _apply(mode) {
    const current = mode === 'current';
    const period = currentReportingPeriod();
    if (this._start) this._start.value = current ? period.start : '';
    if (this._end) this._end.value = current ? period.end : '';
    this._years.forEach((field) => { field.readOnly = current; });
    this._render();
  }

  _render() {
    const { start, end } = this._period;
    const period = currentReportingPeriod();
    const both = start !== null && end !== null;

    if (this._start && this._end) {
      const bad = both && start >= end;
      this._start.setCustomValidity(bad ? 'The starting year cannot be equal to or after the ending year.' : '');
      this._end.setCustomValidity(bad ? 'The ending year cannot be equal to or before the starting year.' : '');
    }

    if (this._message) {
      this._message.textContent = both && start < end
        ? `You're filing for ${start} – ${end}`
        : "Enter the years you're reconciling";
    }

    if (this._noticeWrapper && this._notice) {
      const show = this._mode === 'reconcile' && both && start < end && end !== period.end;
      this._noticeWrapper.classList.toggle('is-hidden', !show);
      if (show) {
        this._notice.textContent = `You're filing for ${start} – ${end}, not the current reporting period (${period.start} – ${period.end}).`;
      }
    }
  }
}
if (!window.customElements.get('reporting-period-select')) {
  window.customElements.define('reporting-period-select', ReportingPeriodSelect);
}

const BLOCKED_COMPANY_NAMES = [
  'personal use', 'personal', 'individual', 'self', 'self-employed', 'self employed',
  'none', 'n/a', 'not applicable', 'no company', 'no business', 'private',
  'consumer', 'home', 'test', 'asdf', 'null', 'unknown', 'unemployed', 'student',
  'freelance'
];

function levenshtein(a, b) {
  var m = a.length, n = b.length, dp = [], i, j;
  for (i = 0; i <= m; i++) {
    dp[i] = [i];
    for (j = 1; j <= n; j++) {
      dp[i][j] = i === 0 ? j : a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    }
  }
  return dp[m][n];
}

const isBlockedCompanyName = (value) =>
  BLOCKED_COMPANY_NAMES.some((word) => levenshtein(value, word) <= (word.length >= 8 ? 2 : 1));

function setValidity(field, message) {
  field.setCustomValidity(message);
  if (message) field.reportValidity();
}

document.addEventListener('change', (event) => {
  const field = event.target;
  if (!(field instanceof HTMLElement)) return;

  if (field.matches('[data-company-validate]')) {
    const value = field.value.trim().toLowerCase();
    setValidity(field, value && isBlockedCompanyName(value)
      ? field.getAttribute('data-validate-message') || 'Enter a valid company name.'
      : '');
  } else if (field.matches('[data-country-restrict]')) {
    const allowed = field.getAttribute('data-country-restrict');
    const wrong = field.value && field.value !== allowed;
    if (wrong) field.value = '';
    setValidity(field, wrong
      ? field.getAttribute('data-restrict-message') || `This form only accepts submissions from ${allowed}.`
      : '');
  }
});
